/**
 * HTTP wrapper used by every CloudCost SPA component. Authentication is
 * cookie-based now:
 *
 *   - The browser sends the `cc.sid` (HttpOnly) and `cc.csrf` cookies on every
 *     same-origin request because we pass `credentials: 'include'`.
 *   - For non-safe verbs we echo the `cc.csrf` cookie value in the
 *     `X-CSRF-Token` header so the backend can run the double-submit check.
 *   - We NEVER add an `Authorization: Bearer <token>` header — no access /
 *     refresh / ID token ever lives in client storage.
 *
 * On 401 we drop the in-memory user snapshot and redirect to `/` (the SPA
 * shell will re-render the sign-in screen). We do NOT clear any cookies
 * here; only the server can revoke a session.
 */
import { ROUTES } from '@/urls';
import { removeUserSession } from '@/lib/common';

export const fetchWrapper = {
  get,
  post,
  put,
  delete: _delete,
  postForm,
};

const CSRF_COOKIE = 'cc.csrf';
const CSRF_HEADER = 'X-CSRF-Token';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie ? document.cookie.split(';') : [];
  for (const raw of parts) {
    const [name, ...rest] = raw.trim().split('=');
    if (name === CSRF_COOKIE) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return null;
}

function buildHeaders(method: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCsrfCookie();
    if (csrf) headers[CSRF_HEADER] = csrf;
  }
  return headers;
}

function get(url: string, _body: any = null) {
  const requestOptions: RequestInit = {
    method: 'GET',
    credentials: 'include',
    headers: buildHeaders('GET'),
  };
  return fetch(url, requestOptions).then(handleResponse);
}

function post(url: string, body: any) {
  const requestOptions: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers: buildHeaders('POST'),
    body: JSON.stringify(body),
  };
  return fetch(url, requestOptions).then(handleResponse);
}

function postForm(url: string, form: FormData) {
  const headers: Record<string, string> = {};
  const csrf = readCsrfCookie();
  if (csrf) headers[CSRF_HEADER] = csrf;

  const requestOptions: RequestInit = {
    method: 'POST',
    credentials: 'include',
    headers,
    body: form,
  };
  return fetch(url, requestOptions).then(handleResponse);
}

function put(url: string, body: any) {
  const requestOptions: RequestInit = {
    method: 'PUT',
    credentials: 'include',
    headers: buildHeaders('PUT'),
    body: JSON.stringify(body),
  };
  return fetch(url, requestOptions).then(handleResponse);
}

// prefixed with underscored because delete is a reserved word in javascript
function _delete(url: string, body: any = null) {
  const requestOptions: RequestInit = {
    method: 'DELETE',
    credentials: 'include',
    headers: buildHeaders('DELETE'),
    body: body ? JSON.stringify(body) : undefined,
  };
  return fetch(url, requestOptions).then(handleResponse);
}

// helper functions

function handleResponse(response: Response) {
  return response.text().then((text: string) => {
    let data: any = {};
    try {
      data = text && JSON.parse(text);
    } catch (error) {
      console.error('Error parsing JSON:', error);
    }

    if (!response.ok) {
      if (response.status === 401) {
        // Drop the cached user snapshot so the SPA shell renders the
        // sign-in screen. The server already invalidated/refused the
        // session; we do not touch cookies here.
        removeUserSession();
        if (
          typeof window !== 'undefined' &&
          window.location.pathname !== ROUTES.HOME &&
          window.location.pathname !== ROUTES.LOGIN
        ) {
          window.location.href = ROUTES.HOME;
        }
        return Promise.reject(new Error('Unauthorized'));
      }

      const error = data?.message ?? response.statusText;
      const customError = new Error(error) as any;
      customError.response = {
        status: response.status,
        data,
      };
      return Promise.reject(customError);
    }

    return data;
  });
}
